import { Analytics } from 'app/types'
import * as fnc from './fnc'

interface ChartTransformOptions {
    category?: Analytics
    aggregationType?: string
    dateField?: string
    valueField?: string
    labelField?: string
    limit?: number
    chartType?: string
}

/**
 * Detects if a field name indicates it contains duration values
 */
function isDurationField(fieldName: string): boolean {
    if (!fieldName) return false
    const lowerName = fieldName.toLowerCase()
    return lowerName.includes('duration') || lowerName.includes('time') || lowerName.includes('elapsed')
}

/**
 * Formats milliseconds to a human-readable duration string
 */
function formatDuration(milliseconds: number): string {
    if (!milliseconds || milliseconds < 0) return '0s'
    
    const seconds = Math.floor(milliseconds / 1000)
    const minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)
    const days = Math.floor(hours / 24)
    
    if (days > 0) {
        const remainingHours = hours % 24
        return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`
    } else if (hours > 0) {
        const remainingMinutes = minutes % 60
        return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`
    } else if (minutes > 0) {
        const remainingSeconds = seconds % 60
        return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`
    } else {
        return `${seconds}s`
    }
}

/**
 * Gets the appropriate duration value for chart display
 * Converts milliseconds to seconds/minutes/hours based on scale
 */
function getDurationDisplayValue(milliseconds: number): { value: number, unit: string } {
    if (!milliseconds || milliseconds < 0) return { value: 0, unit: 's' }
    
    const seconds = milliseconds / 1000
    const minutes = seconds / 60
    const hours = minutes / 60
    const days = hours / 24
    
    if (days >= 1) {
        return { value: Math.round(days * 100) / 100, unit: 'd' }
    } else if (hours >= 1) {
        return { value: Math.round(hours * 100) / 100, unit: 'h' }
    } else if (minutes >= 1) {
        return { value: Math.round(minutes * 100) / 100, unit: 'm' }
    } else {
        return { value: Math.round(seconds * 100) / 100, unit: 's' }
    }
}

/**
 * Determines the best unit and scale factor for a dataset of duration values
 */
function getDurationScale(values: number[]): { scale: number, unit: string, unitLabel: string } {
    if (!values || values.length === 0) return { scale: 1000, unit: 's', unitLabel: 'seconds' }
    
    const maxValue = Math.max(...values.filter(v => typeof v === 'number' && v > 0))
    
    if (maxValue < 60000) { // Less than 1 minute in ms
        return { scale: 1000, unit: 's', unitLabel: 'seconds' }
    } else if (maxValue < 3600000) { // Less than 1 hour in ms
        return { scale: 60000, unit: 'm', unitLabel: 'minutes' }
    } else if (maxValue < 86400000) { // Less than 1 day in ms
        return { scale: 3600000, unit: 'h', unitLabel: 'hours' }
    } else {
        return { scale: 86400000, unit: 'd', unitLabel: 'days' }
    }
}

/**
 * Scales duration values to appropriate units
 */
function scaleDurationValue(milliseconds: number, scale: number): number {
    if (!milliseconds || milliseconds < 0) return 0
    return Math.round((milliseconds / scale) * 100) / 100 // Round to 2 decimal places
}

/**
 * Transforms analytics data for use in charts
 * @param data The raw analytics data array
 * @param options Configuration options for the transformation
 * @returns Transformed data ready for chart components
 */
export function transformAnalyticsForChart(data: any[], options: ChartTransformOptions = {}) {
    const {
        category = Analytics.Users,
        dateField = 'dateVisited',
        valueField = 'totalDuration',
        labelField = 'name',
        limit = 15
    } = options

    // Default result structure
    const result = {
        category,
        chartData: [],
        title: '',
        type: null,
        dateKey: 'date',
        dataKey: valueField,
        valueKey: 'value',
        nameKey: 'name',
        labelKey: 'label'
    }

    if (!data || data.length === 0) {
        return result
    }

    // Determine the best chart type for this data
    let chartType = options.chartType || detectChartType(data, { dateField, valueField, labelField })
    result.type = chartType

    // Apply the appropriate transformation based on the chart type
    switch (chartType) {
        case 'line':
            return transformForLineChart(data, { ...options, ...result })

        case 'bar':
            return transformForBarChart(data, { ...options, ...result })

        case 'pie':
            return transformForPieChart(data, { ...options, ...result })

        case 'top':
            return transformForTopChart(data, { ...options, ...result })

        default:
            return result
    }
}

/**
 * Detects the most appropriate chart type for the data
 */
function detectChartType(data: any[], options: { dateField: string, valueField: string, labelField: string }): string {
    const { dateField, valueField, labelField } = options

    // Check if data has date values (for time series)
    const hasDateValues = data.some(item => item[dateField] && new Date(item[dateField]).getTime())

    // Check if data has categorical values
    const hasCategories = data.some(item => item[labelField])

    // Count number of unique categories
    const categoryCount = new Set(data.map(item => item[labelField])).size

    if (hasDateValues) {
        // Time series data is best represented by a line chart
        return 'line'
    } else if (hasCategories) {
        if (categoryCount <= 10) {
            // Small number of categories: good for pie charts
            return 'pie'
        } else {
            // Many categories: better for bar charts
            return 'bar'
        }
    } else {
        // Default to a bar chart
        return 'bar'
    }
}

/**
 * Auto-detects the date field in the data if not specified or if specified field doesn't exist
 */
function detectDateField(data: any[], specifiedDateField?: string): string | null {
    if (!data || data.length === 0) return null
    
    const firstItem = data[0]
    
    // First try the specified field if it exists in the data
    if (specifiedDateField && specifiedDateField in firstItem) {
        return specifiedDateField
    }
    
    // Common date field names to check
    const commonDateFields = ['visitDate', 'date', 'createdAt', 'dateVisited', 'timestamp', 'dateCreated']
    
    for (const field of commonDateFields) {
        if (field in firstItem) {
            const value = firstItem[field]
            // Check if the value looks like a date
            if (value && (new Date(value).getTime() || typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}/))) {
                return field
            }
        }
    }
    
    // If no common field found, look for any field that contains date-like values
    for (const [key, value] of Object.entries(firstItem)) {
        if (value && (new Date(value as string).getTime() || (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}/)))) {
            return key
        }
    }
    
    return null
}

/**
 * Transforms data for a line chart (time series)
 */
function transformForLineChart(data: any[], options: any) {
    const { valueField, category, series = [] } = options
    const result = { ...options }
    const isDuration = isDurationField(valueField)

    // Determine all value fields to aggregate (series keys or single valueField)
    const valueFields = series && series.length > 0
        ? series.map((s: any) => s.dataKey)
        : [valueField]

    // Auto-detect the date field if not found or not specified correctly
    const actualDateField = detectDateField(data, options.dateField)
    
    if (!actualDateField) {
        console.warn('No valid date field found in data for line chart')
        result.chartData = []
        return result
    }

    // Group data by date
    const dateGroups = {}

    // Process each data point
    data.forEach(item => {
        if (!item[actualDateField]) return

        // Format the date
        const dateObj = new Date(item[actualDateField])
        if (isNaN(dateObj.getTime())) return // Skip invalid dates
        
        const dateKey = dateObj.toISOString().split('T')[0]

        // Initialize the date group if needed
        if (!dateGroups[dateKey]) {
            dateGroups[dateKey] = {
                date: dateKey,
                dateObj // Keep the original date object for sorting
            }
            // Initialize all value fields to 0
            valueFields.forEach(vf => {
                dateGroups[dateKey][vf] = 0
            })
        }

        // Add the values for each value field
        valueFields.forEach(vf => {
            const val = item[vf]
            if (typeof val === 'number') {
                dateGroups[dateKey][vf] += val
            } else if (typeof val === 'string') {
                const numVal = Number(val)
                if (!isNaN(numVal)) {
                    dateGroups[dateKey][vf] += Math.max(0, numVal)
                } else {
                    dateGroups[dateKey][vf] = (dateGroups[dateKey][vf] || 0) + 1
                }
            } else if (val !== undefined) {
                dateGroups[dateKey][vf] = (dateGroups[dateKey][vf] || 0) + 1
            } else if (valueFields.length === 1) {
                dateGroups[dateKey]['count'] = (dateGroups[dateKey]['count'] || 0) + 1
                dateGroups[dateKey][vf] = dateGroups[dateKey]['count']
            }
        })
    })

    // Convert to array and sort by date
    let chartData = Object.values(dateGroups)
        .sort((a: any, b: any) => a.dateObj - b.dateObj)
        .map((item: any) => {
            const { dateObj, ...rest } = item
            return {
                ...rest,
                // Format date for display
                formattedDate: dateObj.toLocaleDateString()
            }
        })

    // Handle duration scaling
    let durationScale = null
    if (isDuration) {
        const durationValues = chartData.map(item => item[valueField]).filter(v => typeof v === 'number' && v > 0)
        durationScale = getDurationScale(durationValues)
        
        // Scale the values for chart display but keep original for formatted labels
        chartData = chartData.map(item => {
            const originalValue = item[valueField]
            return {
                ...item,
                [valueField]: scaleDurationValue(originalValue, durationScale.scale),
                [`${valueField}_original`]: originalValue, // Keep original for tooltips
                [`${valueField}_formatted`]: formatDuration(originalValue) // Format from original value
            }
        })
    }

    result.chartData = chartData
    result.dataKey = (chartData && chartData.length > 0 && valueField in chartData[0]) ? valueField : 'count'
    result.dateKey = 'date'
    result.actualDateField = actualDateField // Store the actual field name used
    result.title = `${category.toString().toTitleCase()} Over Time`
    result.isDuration = isDuration
    result.durationScale = durationScale

    return result
}

/**
 * Transforms data for a bar chart (categorical)
 */
function transformForBarChart(data: any[], options: any) {
    const { labelField, valueField, aggregationType, limit, category, series = [], excludeNullValues = false } = options
    const result = { ...options }
    const isDuration = isDurationField(valueField)

    // If we have series configuration, handle multi-series data
    if (series && series.length > 0) {
        const multiSeriesResult = transformForMultiSeries(data, { ...options, chartType: 'bar' })
        return multiSeriesResult
    }

    // Group data by category
    const categoryGroups = {}

    // Process each data point
    data.forEach(item => {
        const labelValue = item[labelField]
        
        // Skip null/undefined values if excludeNullValues is enabled
        if (excludeNullValues && (labelValue == null || labelValue === undefined || labelValue === '')) {
            return
        }
        
        const key = labelValue || 'Other'

        // Initialize the category group if needed
        if (!categoryGroups[key]) {
            categoryGroups[key] = {
                [labelField]: key,
                [valueField]: 0,
                // count: 0
            }
        }

        // Add the value
        if (typeof item[valueField] === 'number' || valueField.toLowerCase().includes('count')) {
            const numericValue = typeof item[valueField] === 'number' ? item[valueField] : parseFloat(item[valueField])
            if (!isNaN(numericValue)) {
                categoryGroups[key][valueField] += numericValue
            }
        }

        // Increment count
        // categoryGroups[key].count++
    })

    // Convert to array and sort by value descending
    let chartData = Object.values(categoryGroups)
        // .sort((a: any, b: any) => {
        //     const valA = a[valueField] || a.count
        //     const valB = b[valueField] || b.count
        //     return valB - valA
        // })

    // Handle specific aggregation types for registration analytics
    if (aggregationType === 'exit_page_before_registration') {
        result.labelKey = 'pageLink'
        result.dataKey = 'exitCount'
        result.title = 'Exit Pages (Non-Registered Users)'
    } else if (aggregationType === 'registration_rate_by_device') {
        result.labelKey = 'deviceType'
        result.dataKey = 'registrationRate'
        result.title = 'Registration Rate by Device Type'
        result.isPercentage = true
    } else if (aggregationType === 'session_depth_before_registration') {
        result.labelKey = 'pageCountBucket'
        result.dataKey = 'sessionCount'
        result.title = 'Pages Viewed Before Registration'
    } else if (aggregationType === 'page_view_ranking') {
        result.labelKey = 'pageLink'
        result.dataKey = 'pageViews'
        result.title = 'Page View Ranking'
        result.excludeNullValues = true // Enable null value exclusion for page view ranking
    }

    // Handle duration scaling
    let durationScale = null
    if (isDuration) {
        const durationValues = chartData.map(item => item[valueField]).filter(v => typeof v === 'number' && v > 0)
        durationScale = getDurationScale(durationValues)
        
        // Scale the actual values for chart display but format from original
        chartData = chartData.map(item => {
            const originalValue = item[valueField]
            return {
                ...item,
                [valueField]: scaleDurationValue(originalValue, durationScale.scale),
                [`${valueField}_original`]: originalValue, // Keep original for tooltips
                [`${valueField}_formatted`]: formatDuration(originalValue) // Format from original value
            }
        })
    }

    result.chartData = chartData
    result.dataKey = valueField in chartData[0] ? valueField : 'count'
    result.labelKey = labelField in chartData[0] ? labelField : 'label'
    result.title = `${(aggregationType || 'Items').toTitleCase()} by ${labelField.toString().toTitleCase()}`
    result.isDuration = isDuration
    result.durationScale = durationScale

    return result
}

/**
 * Transforms data for a pie chart (proportions)
 */
function transformForPieChart(data: any[], options: any) {
    const { labelField, valueField, aggregationType, category } = options
    const result = { ...options }
    const isDuration = isDurationField(valueField)

    // We can reuse the bar chart transformation and adapt it for pie chart
    const barData = transformForBarChart(data, options)

    // Convert to pie chart format - use the already scaled data from barData
    const chartData = barData.chartData.map((item: any) => ({
        [labelField]: item[labelField],
        [valueField]: item[valueField] || item.count,
        [`${valueField}_original`]: item[`${valueField}_original`], // Keep original for tooltips
        [`${valueField}_formatted`]: item[`${valueField}_formatted`] // Keep formatted for tooltips
    }))

    result.chartData = chartData
    result.valueKey = valueField in chartData[0] ? valueField : 'value'
    result.labelKey = labelField in chartData[0] ? labelField : 'name'
    result.title = `${(aggregationType || category.toString()).toTitleCase()} Distribution`
    result.isDuration = isDuration
    result.durationScale = barData.durationScale

    return result
}

/**
 * Transforms data for multi-series charts
 */
function transformForMultiSeries(data: any[], options: any) {
    const { labelField, series = [], aggregationType, limit, category, chartType } = options
    const result = { ...options }

    // Group data by the label field (e.g., by source, page, etc.)
    const categoryGroups = {}

    // Process each data point
    data.forEach(item => {
        const key = item[labelField] || 'Other'

        // Initialize the category group if needed
        if (!categoryGroups[key]) {
            categoryGroups[key] = {
                [labelField]: key
            }
            
            // Initialize all series values to 0
            series.forEach(s => {
                categoryGroups[key][s.dataKey] = 0
                if (s.isDuration) {
                    categoryGroups[key][`${s.dataKey}_original`] = 0
                }
            })
        }

        // Add values for each series
        series.forEach(s => {
            const value = item[s.dataKey]
            if (typeof value === 'number') {
                categoryGroups[key][s.dataKey] += value
                if (s.isDuration) {
                    categoryGroups[key][`${s.dataKey}_original`] += value
                }
            } else if (typeof value === 'string') {
                const numVal = Number(value)
                if (!isNaN(numVal)) {
                    categoryGroups[key][s.dataKey] += Math.max(0, numVal)
                    if (s.isDuration) {
                        categoryGroups[key][`${s.dataKey}_original`] += Math.max(0, numVal)
                    }
                } else {
                    categoryGroups[key][s.dataKey] = (categoryGroups[key][s.dataKey] || 0) + 1
                }
            } else if (value !== undefined) {
                categoryGroups[key][s.dataKey] = (categoryGroups[key][s.dataKey] || 0) + 1
            }
        })
    })

    // Convert to array and sort - date fields sort chronologically ascending, others by value descending
    const primarySeries = series[0]
    const isDateLabelField = labelField && labelField.toLowerCase().includes('date')

    // Parse a date string that may be YYYY-MM, YYYY-MM-DD, or other formats
    const parseDateKey = (val: string): number => {
        if (!val) return 0
        // YYYY-MM format (e.g. "2025-05") — append -01 so Date can parse it
        if (/^\d{4}-\d{2}$/.test(val)) return new Date(`${val}-01`).getTime()
        // YYYY format
        if (/^\d{4}$/.test(val)) return new Date(`${val}-01-01`).getTime()
        const t = new Date(val).getTime()
        return isNaN(t) ? 0 : t
    }

    // Also detect if the actual values look like dates even if field name doesn't contain 'date'
    const sampleValues = Object.values(categoryGroups).slice(0, 3).map((g: any) => g[labelField])
    const valuesLookLikeDates = sampleValues.every(v => v && /^\d{4}-\d{2}/.test(String(v)))

    let chartData = Object.values(categoryGroups)
        .sort((a: any, b: any) => {
            if (isDateLabelField || valuesLookLikeDates) {
                return parseDateKey(a[labelField]) - parseDateKey(b[labelField])
            }
            const valA = a[primarySeries.dataKey] || 0
            const valB = b[primarySeries.dataKey] || 0
            return valB - valA
        })

    // Apply limit if specified
    if (limit && chartData.length > limit) {
        chartData = chartData.slice(0, limit)
    }

    // Handle duration scaling for each series
    const seriesWithScaling = series.map(s => {
        const seriesResult = { ...s }
        
        if (s.isDuration) {
            const durationValues = chartData.map(item => item[`${s.dataKey}_original`] || item[s.dataKey])
                .filter(v => typeof v === 'number' && v > 0)
            
            if (durationValues.length > 0) {
                const durationScale = getDurationScale(durationValues)
                seriesResult.durationScale = durationScale
                
                // Scale the values for chart display but keep original for tooltips
                chartData = chartData.map(item => {
                    const originalValue = item[`${s.dataKey}_original`] || item[s.dataKey]
                    return {
                        ...item,
                        [s.dataKey]: scaleDurationValue(originalValue, durationScale.scale),
                        [`${s.dataKey}_formatted`]: formatDuration(originalValue)
                    }
                })
            }
        }
        
        return seriesResult
    })

    result.chartData = chartData
    result.series = seriesWithScaling
    result.labelKey = labelField in chartData[0] ? labelField : 'label'
    result.title = `${(aggregationType || 'Multi-Series').toTitleCase()} Comparison by ${labelField.toString().toTitleCase()}`
    
    // For multi-series, we don't set a single dataKey since we have multiple
    result.dataKey = primarySeries.dataKey // Use primary series for backwards compatibility
    
    return result
}

/**
 * Transforms data for a "top items" chart
 */
function transformForTopChart(data: any[], options: any) {
    const { labelField, valueField, aggregationType, limit, category, series = [] } = options
    const result = { ...options }
    const isDuration = isDurationField(valueField)

    // If we have series configuration, handle multi-series data
    if (series && series.length > 0) {
        const multiSeriesResult = transformForMultiSeries(data, { ...options, chartType: 'top' })
        return multiSeriesResult
    }

    // We can reuse the bar chart transformation since the data structure is similar
    const barData = transformForBarChart(data, { ...options, limit })

    result.chartData = barData.chartData
    result.dataKey = barData.dataKey
    result.labelKey = barData.labelKey
    result.title = `Top ${(aggregationType || 'Items').toTitleCase()} by ${(labelField || 'Value').toTitleCase()}`
    result.isDuration = isDuration
    result.durationScale = barData.durationScale

    return result
}